<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import { CircleUser, LogOut } from '@lucide/vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useLogout } from '@/features/auth/mutations/useLogout'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const auth = useAuthStore()
const logout = useLogout()

const userInitials = computed(() => {
  const user = auth.user
  const initials = `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}`

  return (initials || user?.email?.charAt(0) || '?').toUpperCase()
})

function handleSignOut(): void {
  logout.mutate(undefined, { onSettled: () => toast.success(t('navigation.signedOut')) })
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        :aria-label="t('navigation.userMenu')"
        class="focus-visible:ring-ring rounded-full outline-none focus-visible:ring-2"
      >
        <Avatar class="size-9 after:hidden">
          <AvatarFallback class="bg-primary/10 text-primary text-xs font-semibold">{{
            userInitials
          }}</AvatarFallback>
        </Avatar>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-60" align="end" :side-offset="6">
      <DropdownMenuLabel class="text-foreground truncate py-2 text-sm font-normal">{{
        auth.user?.email
      }}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem as-child>
        <RouterLink to="/profile">
          <CircleUser class="size-4" />
          {{ t('navigation.userSettings') }}
        </RouterLink>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem @select="handleSignOut">
        <LogOut class="size-4" />
        {{ t('navigation.signOut') }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
